import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import React, { ChangeEvent, useState } from 'react';
import { useStyles } from './CommentsOutsidePageEditor.styles';
import ArrowCircleRightRoundedIcon from '@mui/icons-material/ArrowCircleRightRounded';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import ClearIcon from '@mui/icons-material/Clear';
import { Prompt } from '../../Prompt/Prompt.component';
import { CommentTypeId } from '../Comments.types';
import { addComment, deleteComment, updateComment } from '../../../services/exams/examTaskGroup/examTaskGroup.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../../Context/Layout.context';
import { useScoring } from '../../../Context/Scoring.context';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { ExamTaskGroupSolutionScoreShelfResponse } from '../../../models/assessment.types';
import { Status } from '../../../services/exams/assessmentSession/assessments.types';
import useSessionUser from '../../../hooks/useSessionUser';

interface CommentsOutsidePageEditor {
  commentProp: string;
  existingComment?: boolean;
  onSave: (comment: string) => Promise<unknown>;
  isActionVisible: boolean;
  rgzId?: string;
  commentId: string;
  canAddComment?: boolean;
  isCommentOnCriterion?: boolean;
  canAddCommentOnCriterion?: boolean;
  setEditedItemId?: (id: string) => void;
  isEnabled?: boolean;
  canEditScore: boolean;
}

const COMMENTS_MAX_LENGTH = 2000;
const COMMENTS_MIN_LENGTH = 32;

export const CommentsOutsidePageEditor: React.FC<CommentsOutsidePageEditor> = ({
  commentProp,
  existingComment,
  onSave,
  isActionVisible,
  rgzId,
  commentId,
  canAddComment,
  isCommentOnCriterion,
  canAddCommentOnCriterion,
  setEditedItemId,
  isEnabled,
  canEditScore,
}) => {
  const classes = useStyles();
  const [comment, setComment] = useState<string>(commentProp ?? '');
  const [hasCommentFocus, setHasCommentFocus] = useState<boolean>(false);
  const [isPromptOpen, setIsPromptOpen] = useState<boolean>(false);
  const { openAlert, showLoader } = useLayoutContext();
  const { isUserKK, isUserKGZ } = useSessionUser();

  const {
    activeCriterion,
    openScoreShelf,
    orgzId,
    examTaskGroupSolutionWithTasks,
    setIsForcedCommentChanged,
    setIsCommentUpdate,
    examTaskGroupSolutionScoreVersionId,
    setExamTaskGroupSolutionScoreVersionId,
    setIsCommentEdit,
  } = useScoring();

  const isCommentToLong = comment.length > COMMENTS_MAX_LENGTH;
  const isCommentToShort = comment.length < COMMENTS_MIN_LENGTH;

  const handleCommentChange = (value: string) => {
    if (isCommentOnCriterion && canAddCommentOnCriterion) {
      setIsCommentEdit(true);
    }
    setComment(value);
  };

  const handleAdd = () => {
    showLoader(true, 'Zapisywanie danych');
    addComment(
      rgzId,
      isCommentOnCriterion && canAddCommentOnCriterion ? CommentTypeId.ONCRITERIA : CommentTypeId.OFFPAGE,
      comment,
      null,
      null,
      null,
      activeCriterion ? activeCriterion.partialScoreId : null,
    )
      .then(
        (response: AxiosResponse<boolean>) => {
          if (response.data) {
            onSave(commentProp);
          } else {
            openAlert('error');
          }
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => {
        setIsCommentEdit(false);
      });
  };

  const scoreStateAssessed =
    examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState &&
    examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState === Status.Closed;

  const saveEdited = () => {
    if (
      orgzId &&
      canEditScore &&
      scoreStateAssessed &&
      !examTaskGroupSolutionScoreVersionId &&
      isCommentOnCriterion &&
      (isUserKGZ || isUserKK)
    ) {
      showLoader(true);
      setIsCommentUpdate(true);
      openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
        const criterionName = activeCriterion!.criterionName;
        const newCommentId = responseData?.commentsDto.find((c) => c.criterionName?.includes(criterionName))?.commentId;
        const newPartialScoreId = responseData?.commentsDto.find((c) =>
          c.criterionName?.includes(criterionName),
        )?.partialScoreId;
        if (newCommentId && newPartialScoreId) {
          return await updateComment(newCommentId, comment, null, null, null, newPartialScoreId)
            .then(
              (response: AxiosResponse<boolean>) => {
                if (response.data) {
                  openAlert('success');
                  if (setEditedItemId !== undefined) {
                    setEditedItemId('');
                  }
                  if (isCommentOnCriterion) {
                    setIsForcedCommentChanged(true);
                  } else showLoader(true);
                } else {
                  openAlert('error');
                }
              },
              () => {
                openAlert('error');
              },
            )
            .finally(() => {
              setIsCommentUpdate(false);
              setIsCommentEdit(false);
            });
        }
      });
    } else {
      showLoader(true);
      updateComment(commentId, comment, null, null, null, activeCriterion ? activeCriterion.partialScoreId : null)
        .then(
          (response: AxiosResponse<boolean>) => {
            if (response.data) {
              if (setEditedItemId !== undefined) {
                setEditedItemId('');
              }
              onSave(commentProp);
            } else {
              openAlert('error');
            }
          },
          () => {
            openAlert('error');
          },
        )
        .finally(() => {
          setIsCommentEdit(false);
        });
    }
  };

  const handleSave = () => {
    if (isEnabled) {
      setComment(comment);
      saveEdited();
    } else {
      handleAdd();
      setComment('');
    }
  };

  const handleCancel = () => {
    if (isEnabled) {
      setComment(commentProp);
      if (setEditedItemId) {
        setEditedItemId('');
      }
    } else {
      setComment('');
    }
  };

  const handleEdit = () => {
    if (setEditedItemId !== undefined) {
      setEditedItemId(commentId);
    }
  };

  const handleRemoveComment = () => {
    if (
      orgzId &&
      canEditScore &&
      scoreStateAssessed &&
      !examTaskGroupSolutionScoreVersionId &&
      isCommentOnCriterion &&
      (isUserKGZ || isUserKK)
    ) {
      showLoader(true);
      setIsCommentUpdate(true);
      setIsPromptOpen(false);
      openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
        const criterionName = activeCriterion!.criterionName;
        const newCommentId = responseData?.commentsDto.find((c) => c.criterionName?.includes(criterionName))?.commentId;
        const newPartialScoreId = responseData?.commentsDto.find((c) =>
          c.criterionName?.includes(criterionName),
        )?.partialScoreId;
        if (newCommentId && newPartialScoreId) {
          return await deleteComment(newCommentId, newPartialScoreId)
            .then(
              (response: AxiosResponse<boolean>) => {
                if (response.data) {
                  setExamTaskGroupSolutionScoreVersionId(undefined);
                  onSave(commentProp);
                  if (setEditedItemId !== undefined) {
                    setEditedItemId('');
                  }
                } else {
                  openAlert('error');
                }
              },
              () => {
                openAlert('error');
              },
            )
            .finally(() => {
              setIsCommentEdit(false);
              setIsCommentUpdate(false);
            });
        }
      });
    } else {
      showLoader(true);
      deleteComment(commentId, activeCriterion ? activeCriterion.partialScoreId : null)
        .then(
          (response: AxiosResponse<boolean>) => {
            if (response.data) {
              if (setEditedItemId !== undefined) {
                setEditedItemId('');
              }
              openAlert('success');
              setIsPromptOpen(false);
              onSave(commentProp);
            } else {
              openAlert('error');
              setIsPromptOpen(false);
            }
          },
          () => {
            openAlert('error');
            setIsPromptOpen(false);
          },
        )
        .finally(() => {
          setIsCommentEdit(false);
        });
    }
  };

  const getActionsMenuIcon = (): JSX.Element => {
    const menuItems: ActionMenuItem[] = [];
    menuItems.push({
      text: 'Edytuj',
      onClick: () => handleEdit(),
    });
    menuItems.push({
      text: 'Usuń',
      onClick: () => setIsPromptOpen(true),
    });
    return <ActionsMenu menuItems={menuItems} />;
  };

  const isFirefox = navigator.userAgent.indexOf('Firefox') != -1;

  const getCommentErrorMessage = () => {
    return isCommentToShort && isCommentOnCriterion && canAddCommentOnCriterion
      ? `Komentarz jest za krótki. Minimalna liczba znaków wynosi ${COMMENTS_MIN_LENGTH}.`
      : isCommentToLong
      ? `Komentarz jest za długi. Maksymalna liczba znaków wynosi ${COMMENTS_MAX_LENGTH}.`
      : '';
  };

  const isCommentError = () => {
    return (
      comment !== '' &&
      ((isCommentOnCriterion && canAddCommentOnCriterion && (isCommentToLong || isCommentToShort)) || isCommentToLong)
    );
  };

  return (
    <div
      className={!isEnabled ? classes.commentEditorContainer : classes.commentEditorContainerEdit}
      onKeyDown={(e) => {
        // prevents firing key shortcuts when typing
        e.stopPropagation();
      }}
    >
      <TextField
        value={comment}
        multiline
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleCommentChange(e.target.value)}
        variant={existingComment ? 'standard' : 'outlined'}
        fullWidth
        focused={isCommentOnCriterion && canAddCommentOnCriterion}
        placeholder={
          isCommentOnCriterion && canAddCommentOnCriterion
            ? 'Dodaj komentarz do ocenianego kryterium'
            : 'Dodaj komentarz...'
        }
        onFocus={() => setHasCommentFocus(true)}
        onBlur={() => setHasCommentFocus(false)}
        error={isCommentError()}
        helperText={
          comment &&
          (!existingComment || isEnabled) && (
            <span
              style={{
                fontSize: '12px',
                position: 'absolute',
                bottom: isEnabled ? '-10px' : '2px',
                color: isCommentError() ? '#BC0000' : undefined,
              }}
            >
              {`${comment.length}/${COMMENTS_MAX_LENGTH} ${isCommentError() ? getCommentErrorMessage() : ''}`}{' '}
            </span>
          )
        }
        inputProps={{
          style: {
            maxHeight: '100px',
            overflow: 'auto',
            paddingBottom: comment && !existingComment && isCommentOnCriterion ? '30px' : '',
          },
        }}
        InputProps={{
          endAdornment: existingComment ? (
            isEnabled && (
              <div style={{ display: 'flex' }}>
                <ButtonOne
                  color="primary"
                  variant="outlined"
                  onClick={handleCancel}
                  style={{ margin: '5px' }}
                  aria-label="przycisk anuluj"
                >
                  Anuluj
                </ButtonOne>
                <ButtonOne
                  color="primary"
                  variant="contained"
                  onClick={handleSave}
                  style={{ margin: '5px' }}
                  disabled={commentProp === comment || isCommentToLong || (isCommentOnCriterion && isCommentToShort)}
                  aria-label="przycisk zapisz"
                >
                  Zapisz
                </ButtonOne>
              </div>
            )
          ) : (
            <InputAdornment position="end">
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div style={{ alignSelf: 'flex-end', paddingRight: '8px' }}>
                  {comment && (
                    <TooltipOne title="Anuluj wprowadzanie komentarza">
                      <IconButton disableRipple onClick={handleCancel} className={classes.xIcon}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </TooltipOne>
                  )}
                </div>
                <div>
                  <TooltipOne
                    title={
                      isCommentError() || !canAddComment
                        ? 'Zatwierdzenie komentarza nie jest w tej chwili możliwe'
                        : 'Zatwierdź komentarz'
                    }
                  >
                    <IconButton
                      disableRipple
                      onClick={handleSave}
                      disabled={isCommentError() || !canAddComment || !comment.length}
                      className={classes.arrowIcon}
                      aria-label="przycisk zapisz"
                    >
                      <ArrowCircleRightRoundedIcon fontSize="large" />
                    </IconButton>
                  </TooltipOne>
                </div>
              </div>
            </InputAdornment>
          ),
          readOnly: (existingComment && !isEnabled) || (!isEnabled && !canAddComment) ? true : false,
        }}
        className={
          existingComment && !hasCommentFocus && !isEnabled
            ? isFirefox
              ? classes.ellipsisTextMoz
              : classes.ellipsisText
            : ''
        }
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          color: isCommentToLong ? '#BC0000' : undefined,
        }}
      >
        {existingComment && isActionVisible && <div className={classes.actionIcon}>{getActionsMenuIcon()}</div>}
      </div>
      {isPromptOpen && (
        <Prompt
          type="confirmation"
          mainActionButtonText="TAK"
          open={true}
          mainText="Czy na pewno chcesz usunąć komentarz?"
          onConfirm={handleRemoveComment}
          onCancel={() => setIsPromptOpen(false)}
        />
      )}
    </div>
  );
};
